import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import { COUNTRY_DEFAULTS } from '../lib/constants';

// Plain axios instance — no interceptors, no auth redirect.
// Country config is public data used on both authenticated and public pages.
const publicHttp = axios.create({ baseURL: API_BASE_URL, timeout: 8000 });

// Static TN fallback config — used while loading or on API error
const TN_FALLBACK = {
    code: 'TN',
    name_fr: 'Tunisie',
    name_en: 'Tunisia',
    flag: '🇹🇳',
    currency: { code: 'TND', symbol: 'DT', decimal_places: 3, decimal_separator: ',', thousands_separator: ' ' },
    timezone: 'Africa/Tunis',
    locale: 'fr-TN',
    phone: { dial_code: '+216', placeholder: '+216 XX XXX XXX' },
    legal_forms: ['SARL', 'SUARL', 'SA', 'ASSOCIATION', 'OTHER'],
    legal_identifier: { field: 'matricule_fiscal', label_fr: 'Matricule Fiscal', label_en: 'Tax ID (Matricule Fiscal)', label_ar: 'المعرف الجبائي', required: true, placeholder: '1883981/P/A/M/000', regex: '^\\d{5,7}\\/?[A-Z](\\/[A-Z]\\/[A-Z]\\/\\d{3})?$' },
    secondary_identifiers: [],
    tax: { vat_rate: 0.19, vat_label: 'TVA', social_security_label: 'CNSS', social_security_employee_rate: 0.0935, social_security_employer_rate: 0.229 },
    working_time: { hours_per_week: 40, days_per_week: 6, hours_per_month: 173.33, saturday_working: true },
    leave: { annual_leave_days: 26, maternity_leave_days: 98, paternity_leave_days: 3 },
    payroll: { minimum_wage: 400, enable_eid_bonus: true, eid_bonus_amount: 200 },
    accounting: { gl_salary_account: '6411', gl_payable_account: '4281', gl_social_security_account: '4291' },
    employee_fields: { show_is_postier: true, show_regime: true },
};

// Full static configs per country — avoids TN defaults bleeding into other countries on API failure
const STATIC_CONFIGS = {
    TN: TN_FALLBACK,
    QA: {
        ...TN_FALLBACK,
        code: 'QA', name_fr: 'Qatar', name_en: 'Qatar', flag: '🇶🇦',
        currency: { code: 'QAR', symbol: 'QAR', decimal_places: 2, decimal_separator: '.', thousands_separator: ',' },
        timezone: 'Asia/Qatar', locale: 'ar-QA',
        phone: { dial_code: '+974', placeholder: '+974 XXXX XXXX' },
        legal_forms: ['WLL', 'QSC', 'LLC', 'OTHER'],
        legal_identifier: { field: 'cr_number', label_fr: 'Numéro CR (Registre Commercial)', label_en: 'CR Number (Commercial Registration)', label_ar: 'رقم السجل التجاري', required: true, placeholder: 'XXXXXXXX', regex: null },
        secondary_identifiers: [],
        tax: { vat_rate: 0.0, vat_label: 'TVA', social_security_label: 'GPSSA', social_security_employee_rate: 0.0, social_security_employer_rate: 0.0 },
        working_time: { hours_per_week: 48, days_per_week: 6, hours_per_month: 208.0, saturday_working: true },
        payroll: { minimum_wage: 0, enable_eid_bonus: false, eid_bonus_amount: 0 },
        employee_fields: { show_is_postier: false, show_regime: false },
    },
    MA: {
        ...TN_FALLBACK,
        code: 'MA', name_fr: 'Maroc', name_en: 'Morocco', flag: '🇲🇦',
        currency: { code: 'MAD', symbol: 'MAD', decimal_places: 2, decimal_separator: '.', thousands_separator: ',' },
        timezone: 'Africa/Casablanca', locale: 'fr-MA',
        phone: { dial_code: '+212', placeholder: '+212 XX XXX XXXX' },
        legal_forms: ['SARL', 'SARLAU', 'SA', 'SNC', 'EI', 'OTHER'],
        legal_identifier: { field: 'ice', label_fr: 'ICE (Identifiant Commun de l\'Entreprise)', label_en: 'ICE (Common Business Identifier)', label_ar: 'المعرف المشترك للمقاولة (ICE)', required: true, placeholder: '000000000000000', regex: '^\\d{15}$' },
        secondary_identifiers: [
            { field: 'if_number', label_fr: 'IF (Identifiant Fiscal)' },
            { field: 'rc_number', label_fr: 'RC (Registre du Commerce)' },
        ],
        tax: { vat_rate: 0.20, vat_label: 'TVA', social_security_label: 'CNSS', social_security_employee_rate: 0.0448, social_security_employer_rate: 0.2193 },
        working_time: { hours_per_week: 44, days_per_week: 6, hours_per_month: 191.0, saturday_working: true },
        leave: { annual_leave_days: 18, maternity_leave_days: 98, paternity_leave_days: 3 },
        payroll: { minimum_wage: 3111, enable_eid_bonus: false, eid_bonus_amount: 0 },
        employee_fields: { show_is_postier: false, show_regime: true },
    },
    FR: {
        ...TN_FALLBACK,
        code: 'FR', name_fr: 'France', name_en: 'France', flag: '🇫🇷',
        currency: { code: 'EUR', symbol: '€', decimal_places: 2, decimal_separator: ',', thousands_separator: ' ' },
        timezone: 'Europe/Paris', locale: 'fr-FR',
        phone: { dial_code: '+33', placeholder: '+33 X XX XX XX XX' },
        legal_forms: ['SARL', 'SA', 'SAS', 'EURL', 'EI', 'AUTO_ENTREPRENEUR', 'ASSOCIATION', 'OTHER'],
        legal_identifier: { field: 'siret', label_fr: 'SIRET', label_en: 'SIRET', label_ar: 'رقم التسجيل (SIRET)', required: true, placeholder: '12345678901234', regex: '^\\d{14}$' },
        secondary_identifiers: [
            { field: 'siren', label_fr: 'SIREN' },
            { field: 'ape_code', label_fr: 'Code APE' },
            { field: 'vat_number', label_fr: 'N° TVA Intracommunautaire' },
        ],
        tax: { vat_rate: 0.20, vat_label: 'TVA', social_security_label: 'URSSAF', social_security_employee_rate: 0.22, social_security_employer_rate: 0.45 },
        working_time: { hours_per_week: 35, days_per_week: 5, hours_per_month: 151.67, saturday_working: false },
        leave: { annual_leave_days: 25, maternity_leave_days: 112, paternity_leave_days: 28 },
        payroll: { minimum_wage: 1767, enable_eid_bonus: false, eid_bonus_amount: 0, enable_13th_month: true },
        employee_fields: { show_is_postier: false, show_regime: true },
    },
    SA: {
        ...TN_FALLBACK,
        code: 'SA', name_fr: 'Arabie Saoudite', name_en: 'Saudi Arabia', flag: '🇸🇦',
        currency: { code: 'SAR', symbol: 'ر.س', decimal_places: 2, decimal_separator: '.', thousands_separator: ',' },
        timezone: 'Asia/Riyadh', locale: 'ar-SA',
        phone: { dial_code: '+966', placeholder: '+966 XX XXX XXXX' },
        legal_forms: ['JSC', 'LLC', 'SC', 'SI', 'OTHER'],
        legal_identifier: { field: 'cr_number', label_fr: 'Numéro RC (Registre Commercial)', label_en: 'CR Number (Commercial Register)', label_ar: 'رقم السجل التجاري', required: true, placeholder: '1010XXXXXX', regex: '^\\d{10}$' },
        secondary_identifiers: [],
        tax: { vat_rate: 0.15, vat_label: 'TVA', social_security_label: 'GOSI', social_security_employee_rate: 0.10, social_security_employer_rate: 0.12 },
        working_time: { hours_per_week: 48, days_per_week: 6, hours_per_month: 208.0, saturday_working: true },
        leave: { annual_leave_days: 21, maternity_leave_days: 60, paternity_leave_days: 3 },
        payroll: { minimum_wage: 4000, enable_eid_bonus: true, eid_bonus_amount: 0 },
        employee_fields: { show_is_postier: false, show_regime: false },
    },
};

/**
 * Hook that loads the full country configuration for a given country code.
 *
 * Usage:
 *   const { config, loading, isReady } = useCountryConfig(countryCode);
 *   const { config } = useCountryConfig();   // reads from company info automatically
 *
 * The hook always returns a valid config object (never null) — it falls back to
 * the static TN_FALLBACK while the API is loading or when the API fails.
 */
export function useCountryConfig(countryCodeOverride) {
    const [config, setConfig] = useState(TN_FALLBACK);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadConfig = async (countryCode) => {
            setLoading(true);
            try {
                const res = await publicHttp.get(`v1/country-config/${countryCode}`);
                if (!cancelled) {
                    setConfig(res.data || TN_FALLBACK);
                    setError(null);
                }
            } catch (_) {
                if (!cancelled) {
                    setConfig(STATIC_CONFIGS[countryCode] || TN_FALLBACK);
                    setError('country-config-unavailable');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        // 1. Explicit override (registration wizard)
        if (countryCodeOverride) {
            loadConfig(countryCodeOverride);
            return () => { cancelled = true; };
        }

        // 2. Try localStorage cache (already populated by useCompanyInfo)
        try {
            const stored = localStorage.getItem('company_info');
            if (stored) {
                const cc = JSON.parse(stored)?.company?.country_code;
                if (cc) { loadConfig(cc); return () => { cancelled = true; }; }
            }
        } catch (_) { /* ignore */ }

        // 3. Fetch company info from API and resolve from there.
        // /companies/ requires auth, so attach the access token (publicHttp
        // has no auth interceptor) — otherwise this 401s and falls back to TN.
        const _tok = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        publicHttp.get('v1/companies/', _tok ? { headers: { Authorization: `Bearer ${_tok}` } } : undefined)
            .then((res) => {
                const cc = res.data?.company?.country_code || 'TN';
                try { localStorage.setItem('company_info', JSON.stringify(res.data)); } catch (_) {}
                if (!cancelled) loadConfig(cc);
            })
            .catch(() => {
                if (!cancelled) loadConfig('TN');
            });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryCodeOverride]);

    return { config, loading, isReady: !loading, error };
}

/**
 * Lightweight hook — fetches the list of supported countries for registration.
 * No authentication required.
 */
export function useSupportedCountries() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        publicHttp.get('v1/country-config/supported')
            .then((res) => setCountries(res.data || []))
            .catch(() => {
                // Static fallback list
                setCountries(Object.entries(COUNTRY_DEFAULTS).map(([code, d]) => ({
                    code,
                    name_fr: d.nameFr,
                    currency_code: d.currency,
                    flag: d.flag,
                })));
            })
            .finally(() => setLoading(false));
    }, []);

    return { countries, loading };
}

export default useCountryConfig;
